import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Zap, CheckCircle2, Circle, ExternalLink, Loader2, ArrowRight } from "lucide-react";

const API_V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const CATEGORY_LABELS = {
  profile: "Profile",
  content: "Content & Media",
  lead: "Lead Conversion",
  trust: "Trust & Compliance",
  community: "Community",
};

/**
 * Dashboard widget — full Horsepower checklist and score.
 * Used inside ListingManagement.jsx as a sub-tab.
 */
export default function HorsepowerDashboard({ slug }) {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("undone"); // undone | all

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    axios.get(`${API_V1}/horsepower/${slug}/dashboard`, { headers: getAuthHeader() })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-12 text-center text-[#94A3B8] flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading your Horsepower Score…</div>;
  if (!data) return <p className="text-[#94A3B8]">Horsepower score unavailable.</p>;

  const { score, level, categories, checklist, next_actions } = data;
  const visible = filter === "undone" ? checklist.filter(c => !c.done) : checklist;
  const doneCount = checklist.filter(c => c.done).length;

  return (
    <div className="space-y-6" data-testid="horsepower-dashboard">
      {/* Score header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl p-6 border border-white/10 bg-gradient-to-br from-[#0F172A] via-[#0B1226] to-[#0F172A]"
      >
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-shrink-0">
            <svg width="100" height="100" viewBox="0 0 100 100" className="-rotate-90">
              <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
              <circle cx="50" cy="50" r="42" fill="none" stroke={level.color} strokeWidth="8" strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 42 * (score / 100)} ${2 * Math.PI * 42}`} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-white leading-none" data-testid="hp-score-value">{score}</span>
              <span className="text-[10px] text-[#94A3B8] uppercase tracking-wider">/100</span>
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <p className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1 flex items-center gap-1.5"><Zap className="w-3 h-3 text-[#F59E0B]" /> Listing Horsepower</p>
            <p className="text-xl font-bold mb-1" style={{ color: level.color }} data-testid="hp-level">{level.label}</p>
            <p className="text-sm text-[#94A3B8]">{doneCount} of {checklist.length} items complete. Keep publishing content, photos, offers, and AI tools to grow your score.</p>
          </div>
        </div>

        {/* Category bars */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-2">
          {Object.entries(categories).map(([key, cat]) => {
            const pct = Math.round((cat.earned / Math.max(1, cat.total)) * 100);
            return (
              <div key={key} className="rounded-md bg-white/5 border border-white/10 p-2">
                <div className="flex items-center justify-between mb-1.5 text-[11px]">
                  <span className="text-[#E2E8F0] font-medium">{CATEGORY_LABELS[key]}</span>
                  <span className="text-[#64748B]">{cat.earned}/{cat.total}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full bg-[#00E5FF]" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        {next_actions.length > 0 && (
          <div className="mt-5 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/30 p-3">
            <p className="text-xs uppercase tracking-wider text-[#FBBF24] mb-2">Next 3 actions to boost your score</p>
            <ul className="space-y-1">
              {next_actions.map((a, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-white">
                  <ArrowRight className="w-3.5 h-3.5 text-[#F59E0B]" />
                  {a}
                </li>
              ))}
            </ul>
          </div>
        )}
      </motion.div>

      {/* Checklist */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold">Optimization Checklist</h3>
          <div className="flex gap-1 text-xs">
            <button
              onClick={() => setFilter("undone")}
              className={`px-3 py-1 rounded-full ${filter === "undone" ? "bg-[#00E5FF]/15 text-[#00E5FF]" : "bg-white/5 text-[#94A3B8] hover:bg-white/10"}`}
              data-testid="hp-filter-undone"
            >To-do ({checklist.length - doneCount})</button>
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1 rounded-full ${filter === "all" ? "bg-[#00E5FF]/15 text-[#00E5FF]" : "bg-white/5 text-[#94A3B8] hover:bg-white/10"}`}
              data-testid="hp-filter-all"
            >All ({checklist.length})</button>
          </div>
        </div>
        <ul className="space-y-2">
          {visible.map((c) => (
            <li
              key={c.id}
              className={`rounded-lg border p-3 flex items-center gap-3 ${c.done ? "bg-[#10B981]/5 border-[#10B981]/20" : "bg-white/5 border-white/10"}`}
              data-testid={`hp-checklist-${c.id}`}
            >
              {c.done
                ? <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                : <Circle className="w-5 h-5 text-[#94A3B8] flex-shrink-0" />}
              <span className={`flex-1 text-sm ${c.done ? "text-[#94A3B8] line-through" : "text-white"}`}>{c.label}</span>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-[#64748B]">
                {CATEGORY_LABELS[c.category]} · +{c.points}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-lg bg-white/5 border border-white/10 p-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-white font-medium">Want this listing in front of more customers?</p>
          <p className="text-xs text-[#94A3B8]">Higher Horsepower Score means better visibility on LocalBest.ai.</p>
        </div>
        <a href={`/business/${slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#00E5FF]/15 text-[#00E5FF] text-sm hover:bg-[#00E5FF]/25 transition" data-testid="hp-preview-link">
          Preview my listing <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}
